import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, switchMap, catchError, throwError, take } from 'rxjs';
import { ClerkService } from 'ngx-clerk';
import { environment } from '../../environments/environment';
import { Clerk } from '@clerk/types';

// Helper function to decode JWT tokens
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function decodeJwt(token: string): { header: any, payload: any } {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[JWT Decoder] Invalid token format');
      return { header: null, payload: null };
    }

    // Decode header (first part)
    const headerBase64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
    const headerJson = atob(headerBase64);
    const header = JSON.parse(headerJson);

    // Decode payload (second part)
    const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);

    return { header, payload };
  } catch (error) {
    console.error('[JWT Decoder] Error decoding token:', error);
    return { header: null, payload: null };
  }
}

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const clerkService = inject(ClerkService);

  // Only intercept requests to our API
  if (!request.url.startsWith(environment.apiUrl)) {
    return next(request);
  }

  // Get the Clerk instance and add the session token to the request
  return clerkService.clerk$.pipe(
    take(1),
    switchMap((clerk: Clerk) => {
      if (!clerk || !clerk.session) {
        return next(request);
      }

      // Get the session token
      return from(
        (clerk.session.getToken()
          .catch((error: any) => {
            console.error('[Auth Interceptor] Error getting token:', error);
            return Promise.resolve<string | null>(null);
          }) as Promise<string | null>),
      ).pipe(
        switchMap((token: string | null) => {
          if (!token) {
            return next(request);
          }

          // Clone the request and add the authorization header
          const authReq = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });

          // Pass the cloned request with the token to the next handler
          return next(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
              console.warn(`[Auth Interceptor] Request failed with status ${error.status}:`, error.message);
              if (error.error) {
                console.warn(`[Auth Interceptor] Error details:`, error.error);
              }
              return throwError(() => error);
            }),
          );
        }),
      );
    }),
    catchError((error: any) => {
      if (error instanceof HttpErrorResponse) {
        return throwError(() => error);
      }

      console.warn('[Auth Interceptor] Error in auth interceptor:', error);
      return next(request);
    }),
  );
};
